import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Where a cell's widgets live: a block's own items (`sectionId` unset) or a section's widgets.
 * Every builder (standard canvas, patient journey grid, stage view) lays widgets out in cells.
 */
export type CellContainer = { blockId: string; sectionId?: string };

/** What's being dragged: a widget from any cell, or a section within its block. */
export type CellDrag =
  | { kind: 'cell-widget'; blockId: string; sectionId: string | null; row: number; slot: number }
  | { kind: 'cell-section'; blockId: string; row: number; slot: number };

const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

/** Drag data can come from outside the page; accept only a well-formed cell drag. */
export function parseCellDrag(raw: string): CellDrag | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return null;
    const d = value as Record<string, unknown>;
    if (typeof d.blockId !== 'string' || !isInt(d.row) || !isInt(d.slot)) return null;
    if (d.kind === 'cell-section') return { kind: 'cell-section', blockId: d.blockId, row: d.row, slot: d.slot };
    if (d.kind !== 'cell-widget') return null;
    if (d.sectionId === null) return { kind: 'cell-widget', blockId: d.blockId, sectionId: null, row: d.row, slot: d.slot };
    if (typeof d.sectionId !== 'string') return null;
    return { kind: 'cell-widget', blockId: d.blockId, sectionId: d.sectionId, row: d.row, slot: d.slot };
  } catch {
    return null;
  }
}

/** Whether `drag` can be dropped into `container`: widgets go anywhere; sections stay in their own block. */
export function canDropIn(drag: CellDrag, container: CellContainer): boolean {
  if (drag.kind === 'cell-widget') return true;
  return container.sectionId === undefined && drag.blockId === container.blockId;
}

type CellDragState = { dragging: CellDrag | null; setDragging: (drag: CellDrag | null) => void };

const CellDragContext = createContext<CellDragState>({ dragging: null, setDragging: () => undefined });

/** Shares "something is being dragged" across cells, so every cell that can take it shows drop slots. */
export function CellDragProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [dragging, setDragging] = useState<CellDrag | null>(null);
  const value = useMemo(() => ({ dragging, setDragging }), [dragging]);
  return <CellDragContext.Provider value={value}>{children}</CellDragContext.Provider>;
}

export function useCellDrag(): CellDragState {
  return useContext(CellDragContext);
}

/** Starts a drag of `drag` from `source` (its whole card is the drag image). */
export function startCellDrag(
  e: React.DragEvent,
  drag: CellDrag,
  source: HTMLElement | null,
  setDragging: (drag: CellDrag | null) => void,
): void {
  e.stopPropagation();
  e.dataTransfer.setData('text/plain', JSON.stringify(drag));
  e.dataTransfer.effectAllowed = 'move';
  if (source) e.dataTransfer.setDragImage(source, 16, 16);
  // Show drop slots after the browser has started the drag; changing the DOM inside dragstart can cancel it.
  window.setTimeout(() => setDragging(drag), 0);
}

/**
 * How a cell offers Add widget while editing:
 * - `slots`: an Add Widget tile in every free slot (and a new row of them when the rows are full).
 * - `button`: one "+ Add widget" button under the widgets.
 */
export type CellAddMode = 'slots' | 'button';

const CellAddModeContext = createContext<CellAddMode>('slots');

export function CellAddModeProvider({ mode, children }: { mode: CellAddMode; children: React.ReactNode }): React.ReactElement {
  return <CellAddModeContext.Provider value={mode}>{children}</CellAddModeContext.Provider>;
}

export function useCellAddMode(): CellAddMode {
  return useContext(CellAddModeContext);
}

export function acceptDrag(e: React.DragEvent): void {
  e.preventDefault();
  e.stopPropagation();
  e.dataTransfer.dropEffect = 'move';
}
